import React, { useEffect, useState } from "react";
import { ExternalLink, MapPin, ShoppingBag } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import {
  EmptyState,
  PanelActionButton,
  SectionPanel,
  StatusPill,
} from "@/components/platform/ContentPrimitives";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/formatters";
import { getDirectoryTypeLabel } from "@/lib/polenica";

function LinkRow({ label, value, href }) {
  if (!value) {
    return null;
  }

  return (
    <a
      href={href || value}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
    >
      {label}: {value}
      <ExternalLink className="h-4 w-4" />
    </a>
  );
}

export default function MarketplacePostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/marketplace/${postId}`);
        setPost(response.data);
      } catch (_error) {
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <div className="platform-elevated-surface rounded-[28px] border px-6 py-12 text-center text-mutedForeground">
          Cargando publicacion...
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <EmptyState
          icon={ShoppingBag}
          title="Publicacion no disponible"
          description="Puede haber sido retirada por su autor, por administración o no existir."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <SectionPanel
        title={post.titulo}
        eyebrow="Detalle del directorio"
        description="Revise la oferta, su ubicacion y los canales de contacto visibles de esta publicacion."
        actions={
          <PanelActionButton tone="secondary" onClick={() => navigate("/directorio")}>
            Volver al directorio
          </PanelActionButton>
        }
      >
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              {(post.imagenes || []).length ? (
                post.imagenes.map((image, index) => (
                  <div
                    key={`${image}-${index}`}
                    className="overflow-hidden rounded-[24px] border border-primary/10 bg-[#EEF4E7]"
                  >
                    <img src={image} alt={`${post.titulo} ${index + 1}`} className="h-full w-full object-cover" />
                  </div>
                ))
              ) : (
                <div className="flex min-h-[260px] items-center justify-center rounded-[24px] border border-primary/10 bg-[#EEF4E7] text-primary/45">
                  <ShoppingBag className="h-16 w-16" />
                </div>
              )}
            </div>

            <div className="platform-soft-panel rounded-[24px] border border-primary/10 p-5">
              <div className="flex flex-wrap gap-2">
                <StatusPill>{getDirectoryTypeLabel(post.tipo_oferta)}</StatusPill>
                <StatusPill tone="neutral">{post.tipo}</StatusPill>
                {(post.enfoques_asociados || []).map((item) => (
                  <StatusPill key={item} tone="neutral">
                    {item}
                  </StatusPill>
                ))}
              </div>
              <p className="mt-4 text-sm leading-7 text-textMain">{post.descripcion}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="platform-elevated-surface rounded-[24px] border p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">Valor</p>
              <p className="mt-3 font-heading text-3xl font-semibold text-primary">
                {post.tipo_valor === "intercambio" ? "Intercambio" : formatCurrency(post.precio)}
              </p>
              {post.busca_intercambio ? (
                <p className="mt-2 text-sm text-mutedForeground">Busca: {post.busca_intercambio}</p>
              ) : null}
            </div>

            <div className="platform-elevated-surface rounded-[24px] border p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">Ubicacion</p>
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-textMain">
                <MapPin className="h-4 w-4 text-primary" />
                {[post.municipio, post.comuna, post.barrio].filter(Boolean).join(" / ") ||
                  "Sin ubicacion visible"}
              </p>
              {post.direccion ? <p className="mt-2 text-sm text-mutedForeground">{post.direccion}</p> : null}
              {post.nombre_iniciativa ? (
                <p className="mt-3 text-sm text-mutedForeground">Iniciativa asociada: {post.nombre_iniciativa}</p>
              ) : null}
            </div>

            <div className="platform-elevated-surface rounded-[24px] border p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">
                Contacto autorizado
              </p>
              <div className="mt-3 space-y-2 text-sm text-textMain">
                <p>{post.nombre_contacto || "No visible"}</p>
                <p>{post.celular_contacto || "Celular no visible"}</p>
                <p>{post.email_contacto || "Correo no visible"}</p>
              </div>
              <div className="mt-4 space-y-2">
                <LinkRow label="Sitio web" value={post.sitio_web} />
                <LinkRow label="Instagram" value={post.instagram_url} />
                <LinkRow label="Facebook" value={post.facebook_url} />
                <LinkRow label="TikTok" value={post.tiktok_handle} />
                <LinkRow label="X" value={post.x_handle} />
                <LinkRow label="WhatsApp" value={post.whatsapp_url} />
              </div>
            </div>
          </div>
        </div>
      </SectionPanel>
    </div>
  );
}
